import { useMemo } from "react";
import { useDowntime } from "@/contexts/DowntimeContext.jsx";
import { useKpiMetrics } from "@/hooks/useKpiMetrics.js";
import { useNoDataStatsRange } from "@/hooks/useNoDataStatsRange.js";
import { flattenTagDataToParameters } from "@/lib/tag-data-utils.js";
import { buildAssetSubpageHref } from "@/lib/asset-data-utils.js";
import { formatNumber } from "@/lib/formatting-utils.js";
import {
  formatAvailabilityPercentWhileLoading,
  resolveCommunicationsAvailabilityPercent,
} from "@/lib/no-data-utils.js";

export const ASSET_KPI_RANGE = "30d";
export const ASSET_KPI_SUBTITLE = "Last 30 days";

/**
 * @typedef {Object} AssetKpiActiveAlarms
 * @property {number} total
 * @property {number} danger
 * @property {number} warned
 */

/**
 * 30-day KPI metrics returned by {@link useKpiMetrics}.
 * @typedef {Object} AssetKpiMetrics
 * @property {number} runtime - Runtime percentage (0–100)
 * @property {number} mechanicalAvailability - Mechanical availability percentage (0–100)
 * @property {number} downtimeEvents - Downtime event count in the last 30 days
 * @property {AssetKpiActiveAlarms} activeAlarms
 */

/**
 * @typedef {Object} AssetKpiFormattedValues
 * @property {string} runtime
 * @property {string} mechanicalAvailability
 * @property {string} communicationsAvailability
 * @property {number} downtimeEvents
 * @property {number} activeAlarmsTotal
 * @property {string} alarmsSubtitle
 */

/**
 * Shared 30-day KPI metrics for asset overview and vitals.
 * @param {Object|null|undefined} compressor
 * @param {{ tagData?: Object|null, allParameters?: Object[] }} [options]
 * @returns {{
 *   kpiMetrics: AssetKpiMetrics,
 *   communicationsAvailabilityDisplay: string,
 *   isLoadingComm: boolean,
 *   alarmsSubtitle: string,
 * }}
 */
export function useAssetKpiSummary(compressor, options = {}) {
  const { tagData, allParameters: allParametersOverride } = options;
  const { downtimeEvents } = useDowntime();

  const allParameters = useMemo(() => {
    if (Array.isArray(allParametersOverride)) {
      return allParametersOverride;
    }
    return flattenTagDataToParameters(tagData);
  }, [allParametersOverride, tagData]);

  const kpiMetrics = useKpiMetrics(compressor, downtimeEvents, allParameters);

  const { entry: noDataStatsEntry } = useNoDataStatsRange({
    selectedRange: ASSET_KPI_RANGE,
    assetId: compressor?.id,
    enabled: Boolean(compressor?.id),
  });

  const communicationsAvailabilityDisplay = formatAvailabilityPercentWhileLoading(
    resolveCommunicationsAvailabilityPercent(
      noDataStatsEntry.data,
      compressor?.id
    ),
    noDataStatsEntry.isLoading
  );

  const alarmsSubtitle = `${kpiMetrics.activeAlarms.danger} Danger, ${kpiMetrics.activeAlarms.warned} Warning`;

  return {
    kpiMetrics,
    communicationsAvailabilityDisplay,
    isLoadingComm: noDataStatsEntry.isLoading,
    alarmsSubtitle,
  };
}

/**
 * @param {AssetKpiMetrics} kpiMetrics
 * @param {string} communicationsAvailabilityDisplay
 * @param {string} alarmsSubtitle
 * @returns {AssetKpiFormattedValues}
 */
export function getAssetKpiFormattedValues(
  kpiMetrics,
  communicationsAvailabilityDisplay,
  alarmsSubtitle
) {
  return {
    runtime: `${formatNumber(kpiMetrics.runtime, 1)}%`,
    mechanicalAvailability: `${formatNumber(kpiMetrics.mechanicalAvailability, 1)}%`,
    communicationsAvailability: communicationsAvailabilityDisplay,
    downtimeEvents: kpiMetrics.downtimeEvents,
    activeAlarmsTotal: kpiMetrics.activeAlarms.total,
    alarmsSubtitle,
  };
}

/**
 * @param {string|number} assetId
 * @param {AssetKpiMetrics} kpiMetrics
 * @param {string} communicationsAvailabilityDisplay
 * @param {string} alarmsSubtitle
 * @param {boolean} [isLoadingComm]
 * @returns {Array<Object>}
 */
export function buildAssetKpiSummaryCells(
  assetId,
  kpiMetrics,
  communicationsAvailabilityDisplay,
  alarmsSubtitle,
  isLoadingComm = false
) {
  const formatted = getAssetKpiFormattedValues(
    kpiMetrics,
    communicationsAvailabilityDisplay,
    alarmsSubtitle
  );
  const downtimeHref = buildAssetSubpageHref(assetId, "downtime-analysis");
  const vitalsHref = buildAssetSubpageHref(assetId, "vitals");
  const trendsHref = buildAssetSubpageHref(assetId, "trends");

  return [
    {
      label: "Runtime",
      value: formatted.runtime,
      subtitle: ASSET_KPI_SUBTITLE,
      href: downtimeHref,
      linkLabel: "View downtime analysis",
      testId: "kpi-runtime",
    },
    {
      label: "Mechanical Availability",
      value: formatted.mechanicalAvailability,
      subtitle: ASSET_KPI_SUBTITLE,
      href: downtimeHref,
      linkLabel: "View downtime analysis",
      testId: "kpi-mechanical-availability",
    },
    {
      label: "Communications Availability",
      value: formatted.communicationsAvailability,
      subtitle: ASSET_KPI_SUBTITLE,
      href: vitalsHref,
      linkLabel: "View asset vitals",
      testId: "kpi-communications-availability",
      isLoading: isLoadingComm,
    },
    {
      label: "Downtime Events",
      value: formatted.downtimeEvents,
      subtitle: ASSET_KPI_SUBTITLE,
      href: downtimeHref,
      linkLabel: "View downtime analysis",
      testId: "kpi-downtime-events",
    },
    {
      label: "Active Alarms",
      value: formatted.activeAlarmsTotal,
      subtitle: formatted.alarmsSubtitle,
      href: trendsHref,
      linkLabel: "View trends",
      testId: "kpi-active-alarms",
    },
  ];
}
